import { Box, Button, MenuItem, Tab, Tabs, TextField, Typography, Table, TableHead, TableRow, TableCell, TableBody, Checkbox } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { handleSave } from "../utils/handleSave";
import { createTempId } from "../utils/createTempId";
import { FIXED_SELECT_FIELD_SX, TABLE_SCROLL_CONTAINER_SX, hasValue, isRowComplete } from "../utils/formHelpers";
import { useKotFormDirty } from "../utils/useKotFormDirty";

const createEmptyBillingState = () => ({
  instructions: [{ id: createTempId(), instructions: "", notes: "" }],
  invoicing: [{ id: createTempId(), recpientName: "", email: "", phone: "", notes: "" }],
  idsBillingArrangements: [{ id: 1, billingArrangement: "", notes: "" }],
});

// Mandatory fields per tab; Notes is optional everywhere.
const BILLING_REQUIRED_FIELDS_BY_TAB = {
  instructions: ["instructions", "notes"],
  invoicing: ["recpientName", "email", "phone", "notes"],
  idsBillingArrangements: ["billingArrangement", "notes"],
};
const BILLING_ALL_FIELDS_BY_TAB = {
  instructions: ["instructions", "notes"],
  invoicing: ["recpientName", "email", "phone", "notes"],
};
const billingRowHasData = (tabKey, row) => (BILLING_ALL_FIELDS_BY_TAB[tabKey] || []).some((field) => hasValue(row?.[field]));

const buildBillingState = (source) => {
  const emptyState = createEmptyBillingState();

  return {
    instructions:
      source?.instructions?.length > 0
        ? source.instructions.map((row) => ({ ...row }))
        : emptyState.instructions,
    invoicing:
      source?.invoicing?.length > 0
        ? source.invoicing.map((row) => ({ ...row }))
        : emptyState.invoicing,
    idsBillingArrangements:
      source?.idsBillingArrangements?.length > 0
        ? source.idsBillingArrangements.map((row) => ({ ...row }))
        : emptyState.idsBillingArrangements,
  };
};

const SelectField = ({ value, onChange, options = [], disabled }) => (
  <TextField disabled={disabled} label="Select" select size="small" fullWidth value={value} onChange={onChange} sx={FIXED_SELECT_FIELD_SX}>
    <MenuItem value="">Select</MenuItem>
    {options.map((opt) => (
      <MenuItem key={opt.value} value={opt.value}>
        {opt.label}
      </MenuItem>
    ))}
  </TextField>
);

const InputField = ({ value, placeholder, onChange, disabled }) => <TextField disabled={disabled} size="small" fullWidth multiline minRows={1} value={value} placeholder={placeholder} onChange={onChange} />;

const Billing = ({ mode }) => {
  const isView = mode === "view";
  const dispatch = useDispatch();
  const savedData = useSelector((state) => state.kotClient.billing);
  const clientId = useSelector((state) => state.kotClient?.clientId);

  const [activeTab, setActiveTab] = useState(0);

  const [data, setData] = useState(buildBillingState(savedData));

  const [selectedRows, setSelectedRows] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const { isDirty, markDirty, markSaved, markReset, syncClean } = useKotFormDirty();
  const syncedClientIdRef = useRef(null);

  useEffect(() => {
    // Rebuild from the store on client switch or while the form is clean
    // (initial fetch, post-save/cancel sync). Skip while dirty: saveData()
    // runs three sequential handleSave calls, and the Redux update from an
    // earlier one must not wipe unsaved edits in the other tabs or clear the
    // dirty flag before the remaining sub-saves finish.
    const clientChanged = syncedClientIdRef.current !== clientId;
    if (!clientChanged && isDirty) return;
    syncedClientIdRef.current = clientId;

    setData(buildBillingState(savedData));
    setSelectedRows({});
    syncClean();
  }, [clientId, savedData, isDirty, syncClean]);

  const handleSelectRow = (tabKey, id) => {
    setSelectedRows((prev) => {
      const current = prev[tabKey] || [];
      return {
        ...prev,
        [tabKey]: current.includes(id) ? current.filter((r) => r !== id) : [...current, id],
      };
    });
  };

  const handleSelectAll = (tabKey, checked) => {
    setSelectedRows((prev) => ({
      ...prev,
      [tabKey]: checked ? data[tabKey].map((r) => r.id) : [],
    }));
  };

  const deleteSelectedRows = (tabKey) => {
    setData((prev) => ({
      ...prev,
      [tabKey]: prev[tabKey].filter((row) => !(selectedRows[tabKey] || []).includes(row.id)),
    }));

    setSelectedRows((prev) => ({
      ...prev,
      [tabKey]: [],
    }));
    markDirty();
  };

  const tabsConfig = useMemo(
    () => [
      {
        key: "instructions",
        label: "Instructions",
        columns: [
          {
            field: "instructions",
            headerName: "Instructions",
            width: 360,
            options: [
              { label: "Pencils Down (Attorney Instruction)", value: "Pencils Down (Attorney Instruction)" },
              { label: "Red Hold (Accounting Status)", value: "Red Hold (Accounting Status)" },
              { label: "Advanced Invoicing Required", value: "Advanced Invoicing Required" },
              { label: "Other", value: "Other" },
            ],
          },
          { field: "notes", headerName: "Notes", width: 280 },
        ],
      },
      {
        key: "invoicing",
        label: "Invoicing",
        columns: [
          { field: "recpientName", headerName: "Invoice Recipient Name", width: 240 },
          { field: "email", headerName: "Email Address", width: 280 },
          { field: "phone", headerName: "Phone No.", width: 180 },
          { field: "notes", headerName: "Notes", width: 280 },
        ],
      },
      {
        key: "idsBillingArrangements",
        label: "IDS Billing Arrangements",
        columns: [
          {
            field: "billingArrangement",
            headerName: "IDS Billing Arrangement",
            width: 240,
            options: [
              { label: "Fixed", value: "Fixed" },
              { label: "Hourly", value: "Hourly" },
            ],
          },
          { field: "notes", headerName: "Notes", width: 280 },
        ],
      },
    ],
    [],
  );

  const currentTab = tabsConfig[activeTab];
  const currentKey = currentTab.key;
  const currentRows = data[currentKey];
  const currentSelected = selectedRows[currentKey] || [];
  const instructionOptions = useMemo(() => tabsConfig.find((tab) => tab.key === "instructions")?.columns.find((column) => column.field === "instructions")?.options || [], [tabsConfig]);
  const selectedInstructionValues = useMemo(() => data.instructions.map((row) => row.instructions).filter(Boolean), [data.instructions]);

  const handleInputChange = (tabKey, rowId, field, value) => {
    if (tabKey === "instructions" && field === "instructions") {
      const duplicateSelection = data.instructions.some((row) => row.id !== rowId && row.instructions === value);
      if (duplicateSelection) {
        toast.warn("Each billing instruction can only be selected once.");
        return;
      }
    }

    setData((prev) => ({
      ...prev,
      [tabKey]: prev[tabKey].map((row) => (row.id === rowId ? { ...row, [field]: value } : row)),
    }));
    markDirty();
  };

  const handleAddMore = () => {
    if (currentKey === "idsBillingArrangements") return; // 1-to-1
    // Allow a new row only when existing rows have all mandatory fields filled (Notes optional).
    if (currentRows.some((row) => !isRowComplete(row, BILLING_REQUIRED_FIELDS_BY_TAB[currentKey] || []))) {
      toast.warn("Complete the mandatory fields in the current row before adding another.");
      return;
    }
    if (currentKey === "instructions" && data.instructions.length >= instructionOptions.length) {
      toast.warn("All instruction options are already selected.");
      return;
    }

    setData((prev) => ({
      ...prev,
      [currentKey]: [
        ...prev[currentKey],
        {
          ...(currentKey === "instructions"
            ? { id: createTempId(), instructions: "", notes: "" }
            : { id: createTempId(), recpientName: "", email: "", phone: "", notes: "" }),
        },
      ],
    }));
    markDirty();
  };

  const renderCell = (row, col) => {
    if (col.options) {
      const options =
        currentKey === "instructions" && col.field === "instructions"
          ? col.options.filter((option) => option.value === row.instructions || !selectedInstructionValues.includes(option.value))
          : col.options;

      return <SelectField value={row[col.field]} options={options} onChange={(e) => handleInputChange(currentKey, row.id, col.field, e.target.value)} disabled={isView} />;
    }

    return <InputField value={row[col.field]} placeholder={col.headerName} onChange={(e) => handleInputChange(currentKey, row.id, col.field, e.target.value)} disabled={isView} />;
  };

  const saveData = async () => {
    const uniqueInstructionValues = new Set(selectedInstructionValues);
    if (selectedInstructionValues.length !== uniqueInstructionValues.size) {
      toast.error("Billing instructions must be unique.");
      return;
    }

    if (data.instructions.length > instructionOptions.length) {
      toast.error(`Only ${instructionOptions.length} instruction rows are allowed.`);
      return;
    }

    // Validate mandatory fields on submit for any started row (empty rows are ignored).
    const hasIncompleteStartedRow =
      data.instructions.some((row) => billingRowHasData("instructions", row) && !isRowComplete(row, BILLING_REQUIRED_FIELDS_BY_TAB.instructions)) ||
      data.invoicing.some((row) => billingRowHasData("invoicing", row) && !isRowComplete(row, BILLING_REQUIRED_FIELDS_BY_TAB.invoicing));
    if (hasIncompleteStartedRow) {
      toast.error("Please complete all mandatory fields before saving.");
      return;
    }

    setIsSaving(true);
    try {
      // Instructions: 1-to-N
      const instructionsResult = await handleSave(dispatch, "billingInstructions", data.instructions, clientId, savedData?.instructions || [], { showSuccessToast: false });
      // Invoicing Recipients: 1-to-N
      const invoicingResult = await handleSave(dispatch, "billingInvoicingRecipients", data.invoicing, clientId, savedData?.invoicing || [], { showSuccessToast: false });
      // IDS Billing Arrangement: 1-to-1
      const idsResult = await handleSave(dispatch, "billingIdsArrangement", data.idsBillingArrangements?.[0] || {}, clientId, savedData?.idsBillingArrangements?.[0] || {}, { showSuccessToast: false });

      const results = [instructionsResult, invoicingResult, idsResult];
      if (results.some((result) => result?.status === "error")) {
        return;
      }

      markSaved();

      if (results.some((result) => result?.status === "saved")) {
        toast.success("Billing & Invoicing saved successfully.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    markReset();
    setData(buildBillingState(savedData));
    setSelectedRows({});
    setActiveTab(0);
  };

  return (
    <Box p={4} bgcolor="#fff" borderRadius={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h3">
          Billing & Invoicing
        </Typography>

        {!isView && currentKey !== "idsBillingArrangements" && currentSelected.length > 0 && (
          <Button color="error" variant="contained" onClick={() => deleteSelectedRows(currentKey)}>
            Delete ({currentSelected.length})
          </Button>
        )}
      </Box>

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
        {tabsConfig.map((tab) => (
          <Tab key={tab.key} label={tab.label} />
        ))}
      </Tabs>

      {/* Table */}
      <Box sx={{ ...TABLE_SCROLL_CONTAINER_SX, mt: 2 }}>
        <Table sx={{ minWidth: 720, tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              {!isView && currentKey !== "idsBillingArrangements" && (
                <TableCell padding="checkbox" sx={{ width: 56 }}>
                  <Checkbox data-ignore-dirty="true" checked={currentRows.length > 0 && currentSelected.length === currentRows.length} indeterminate={currentSelected.length > 0 && currentSelected.length < currentRows.length} onChange={(e) => handleSelectAll(currentKey, e.target.checked)} />
                </TableCell>
              )}

              {currentTab.columns.map((col) => (
                <TableCell key={col.field} sx={{ width: col.width, minWidth: col.width }}>
                  {col.headerName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {currentRows.map((row) => (
              <TableRow key={row.id} selected={currentSelected.includes(row.id)}>
                {!isView && currentKey !== "idsBillingArrangements" && (
                  <TableCell padding="checkbox" sx={{ width: 56 }}>
                    <Checkbox data-ignore-dirty="true" checked={currentSelected.includes(row.id)} onChange={() => handleSelectRow(currentKey, row.id)} />
                  </TableCell>
                )}

                {currentTab.columns.map((col) => (
                  <TableCell key={col.field} sx={{ width: col.width, minWidth: col.width, verticalAlign: "top" }}>
                    {renderCell(row, col)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {!isView && currentKey !== "idsBillingArrangements" && (
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button variant="outlined" onClick={handleAddMore}>
            Add More
          </Button>
        </Box>
      )}

      {!isView && (
        <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
          <Button onClick={handleCancel} disabled={isSaving || !isDirty}>Cancel</Button>
          <Button variant="contained" onClick={saveData} disabled={isSaving || !isDirty}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default Billing;
